import React, { useState } from "react";
import { useDispatch } from "react-redux";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { ActionButton } from "components";
import BlanketPriceChangeDialog from "./BlanketPriceChangeDialog";
import {
  DataTypeEnum,
  DomainEnum,
} from "store/reducers/products/properties/pl-properties.types";
import { editTableAction } from "../../../../store/reducers/products/tables/tables.actions";

interface BlanketPriceChangeButtonProps {
  ruleTableData: any;
}

export const BlanketPriceChangeButton = ({
  ruleTableData,
}: BlanketPriceChangeButtonProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const dispatch = useDispatch();

  const propertyData = ruleTableData.properties.filter(
    (property) =>
      [DataTypeEnum.FLOAT, DataTypeEnum.INT].includes(property.dataType) &&
      [DomainEnum.OPEN, DomainEnum.RANGE].includes(property.domain)
  );

  const propertyOptions = propertyData.map((property) => ({
    value: property.id,
    label: property.name,
    dataType: property.dataType,
  }));

  const handleOpen = () => setDialogOpen(true);
  const handleClose = () => setDialogOpen(false);

  const getPropertyDataType = (ruletableData, propertyId) => {
    const property = ruletableData.properties.find(
      (property) => property.id === propertyId
    );
    return property.dataType;
  };

  const increaseValueByPercent = (
    originalValue: number,
    dataType: DataTypeEnum = DataTypeEnum.INT,
    percent: number,
    roundDigits = 0,
    increaseDeductPrice = false
  ): number => {
    // Only allow FLOAT or INT
    if (dataType !== DataTypeEnum.FLOAT && dataType !== DataTypeEnum.INT) {
      throw new Error(
        `Unsupported data type: ${dataType}. Only FLOAT and INT are allowed.`
      );
    }

    let adjustedPercent = percent;

    if (increaseDeductPrice && originalValue < 0) {
      adjustedPercent = -percent;
    }

    let newValue = originalValue * (1 + adjustedPercent / 100);

    const effectiveDigits = dataType === DataTypeEnum.INT ? 0 : roundDigits;
    const multiplier = Math.pow(10, effectiveDigits);

    newValue =
      Math.round((newValue + Number.EPSILON) * multiplier) / multiplier;

    return newValue;
  };

  const handleSubmit = async (
    selectedPropertyId: string,
    percent: number,
    roundDigits: number,
    increaseDeductPrice: boolean
  ) => {
    if (ruleTableData.id && selectedPropertyId && percent) {
      const dataType = getPropertyDataType(ruleTableData, selectedPropertyId);
      const newRows = ruleTableData.rows.map((row) => {
        const newCells = row.cells.map((cell) => {
          if (cell.propertyId === selectedPropertyId && cell.value[0]) {
            const value = parseFloat(cell.value[0]);
            const newValue = increaseValueByPercent(
              value,
              dataType,
              percent,
              roundDigits,
              increaseDeductPrice
            );
            return {
              ...cell,
              value: [newValue.toString()],
            };
          }
          return cell;
        });
        return { ...row, cells: newCells };
      });

      dispatch(
        editTableAction({
          tableId: ruleTableData.id,
          updateData: { rows: newRows },
        })
      );

      setDialogOpen(false);
    }
  };

  if (!propertyData.length) return null;

  return (
    <>
      <ActionButton
        tooltipText="Blanket Price Change"
        tooltipArrow
        tooltipPlacement="bottom"
        ariaLabel="Blanket price change"
        icon={AttachMoneyIcon}
        iconOnly
        size="medium"
        color="primary"
        onClick={handleOpen}
      />
      {dialogOpen && (
        <BlanketPriceChangeDialog
          open={dialogOpen}
          propertyOptions={propertyOptions}
          onClose={handleClose}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
};

export default BlanketPriceChangeButton;
